import React from "react";
import { format } from "date-fns";
import { Link, Navigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";

import { H2, Span } from "../components/Typography/Typography";
import { LoadingSpinner } from "../components/LoadingSpinner/LoadingSpinner";
import { EmptyStateBox } from "../components/EmptyStateBox/EmptyStateBox";
import { useProfile } from "../contexts/ProfileContext";
import { albumPath, Recommendation } from "../api/albums";

import "./Profile.scss";

type GroupedRecommendation = {
    album_id: number;
    title: string;
    artist_name: string;
    source_cover_url: string | null;
    recommendations: Recommendation[];
};

function groupByAlbum(recommendations: Recommendation[]): GroupedRecommendation[] {
    const groups = new Map<number, GroupedRecommendation>();

    for (const rec of recommendations) {
        const existing = groups.get(rec.album_id);

        if (existing) {
            existing.recommendations.push(rec);
            continue;
        }

        groups.set(rec.album_id, {
            album_id: rec.album_id,
            title: rec.title,
            artist_name: rec.artist_name,
            source_cover_url: rec.source_cover_url,
            recommendations: [rec],
        });
    }

    return Array.from(groups.values());
}

function formatListenedDate(recommendations: Recommendation[]) {
    const latest = recommendations.reduce((current, rec) =>
        new Date(rec.updated_on) > new Date(current.updated_on) ? rec : current
    );

    return `Listened ${format(new Date(latest.updated_on), "MMM d, yyyy")}`;
}

function formatSentStatus(recommendations: Recommendation[]) {
    const hasQueue = recommendations.some((rec) => rec.status === "queue");

    if (hasQueue) {
        return "On their queue";
    }

    const latest = recommendations.reduce((current, rec) =>
        new Date(rec.updated_on) > new Date(current.updated_on) ? rec : current
    );

    return `They listened ${format(new Date(latest.updated_on), "MMM d, yyyy")}`;
}

function AlbumCover({ coverUrl }: { coverUrl: string | null }) {
    if (coverUrl) {
        return <img className="album-recommendations__cover" src={coverUrl} alt="" />;
    }

    return <div className="album-recommendations__cover album-recommendations__cover--placeholder" />;
}

function FriendHistoryColumn({
    title,
    recommendations,
    isLoading,
    emptyMessage,
    subtitleForGroup,
}: {
    title: string;
    recommendations: Recommendation[];
    isLoading: boolean;
    emptyMessage: string;
    subtitleForGroup: (group: GroupedRecommendation) => string;
}) {
    if (isLoading) {
        return (
            <div className="friend-exchange__column">
                <H2 className="friend-exchange__title" variant="heading-4">
                    {title}
                </H2>
                <LoadingSpinner />
            </div>
        );
    }

    if (recommendations.length === 0) {
        return (
            <div className="friend-exchange__column">
                <H2 className="friend-exchange__title" variant="heading-4">
                    {title}
                </H2>
                <EmptyStateBox>{emptyMessage}</EmptyStateBox>
            </div>
        );
    }

    const items = groupByAlbum(recommendations);

    return (
        <div className="friend-exchange__column">
            <H2 className="friend-exchange__title" variant="heading-4">
                {title}
            </H2>
            <ul className="album-recommendations__list">
                {items.map((group) => (
                    <li key={group.album_id} className="album-recommendations__item">
                        <Link
                            className="album-recommendations__link"
                            to={albumPath(group.album_id)}
                        >
                            <AlbumCover coverUrl={group.source_cover_url} />
                            <div className="album-recommendations__meta">
                                <Span variant="body-3" weight="bold">
                                    {group.title}
                                </Span>
                                <Span variant="body-4" color="mid-contrast">
                                    {group.artist_name}
                                </Span>
                                <Span variant="body-4" color="mid-contrast">
                                    {subtitleForGroup(group)}
                                </Span>
                            </div>
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function FriendExchangeTab() {
    const { user, isLoaded } = useUser();
    const { fullName, isFriend, isCurrentUser, userData, receivedFromFriend, sentToFriend, isLoadingFriendExchange } =
        useProfile();

    if (isCurrentUser) {
        return <Navigate to={`/${userData.username}/listened`} replace />;
    }

    if (!isLoaded) {
        return <LoadingSpinner />;
    }

    if (!user) {
        return (
            <section className="profile-content" aria-label="Recommendation history">
                <EmptyStateBox>
                    <Link to="/login">Sign in</Link> to see your recommendation history with{" "}
                    {fullName}.
                </EmptyStateBox>
            </section>
        );
    }

    if (!isFriend) {
        return (
            <section className="profile-content" aria-label="Recommendation history">
                <EmptyStateBox>
                    Add {fullName} as a friend to see your recommendation history together.
                </EmptyStateBox>
            </section>
        );
    }

    return (
        <section className="profile-content friend-exchange" aria-label="Recommendation history">
            <FriendHistoryColumn
                title="Shared with you"
                recommendations={receivedFromFriend}
                isLoading={isLoadingFriendExchange}
                emptyMessage={`You haven't listened to any albums ${fullName} shared with you yet.`}
                subtitleForGroup={(group) => formatListenedDate(group.recommendations)}
            />
            <FriendHistoryColumn
                title="Shared by you"
                recommendations={sentToFriend}
                isLoading={isLoadingFriendExchange}
                emptyMessage={`You haven't shared any albums with ${fullName} yet.`}
                subtitleForGroup={(group) => formatSentStatus(group.recommendations)}
            />
        </section>
    );
}
